import { toolkitUrls, goalbookUrls } from '@/util/links'
import { getHandleRequest } from '@/authentication/request_handler'

export interface Teacher {
  uuid: string
  name: string
  email: string
  title: string
  type: string
  sub_type: string
  end_date: string
  activated: boolean
  schools?: string[]
}

export interface School {
  uuid: string
  name: string
}

export interface TeachersSegmented {
  active_enterprise: Teacher[]
  active_trial: Teacher[]
}

export interface AdminSchoolsResponse {
  schools: School[]
  is_domain_admin: boolean
  is_super_admin: boolean
  super_admin_domain_uuid?: string
}

export interface TeachersResponse {
  domain: {
    uuid: string
    name: string
  }
  school_uuid: string
  teachers: TeachersSegmented
}

export async function getAdminSchools(analyticsDomainUuid?: string): Promise<AdminSchoolsResponse> {
  let url = `${import.meta.env.VITE_TOOLKIT_URL}/admin/api/schools`
  
  // Add analytics_domain_uuid parameter if provided
  if (analyticsDomainUuid) {
    url += `?analytics_domain_uuid=${encodeURIComponent(analyticsDomainUuid)}`
  }
  
  const headers = new Headers({
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  })
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers
  })
  const responseJSON = await response.json()
  if (response.status >= 200 && response.status < 300) {
    return responseJSON
  }
  const error = new Error(`Admin API request failed with status ${response.status}`)
  Object.assign(error, {
    statusCode: response.status,
    responseJSON: responseJSON
  })
  throw error
}

async function getTeachersForSchoolRequest(domainUuid: string, schoolUuid: string): Promise<Response> {
  return await fetch(`${goalbookUrls.pathwaysApi}/domain/${domainUuid}/users/detailed?school=${schoolUuid}`, {
    method: 'GET',
    credentials: 'include'
  })
}

export async function getTeachersForSchool(domainUuid: string, schoolUuid: string): Promise<TeachersResponse> {
  const handleRequest = getHandleRequest()
  return handleRequest(
    { requestFn: getTeachersForSchoolRequest },
    domainUuid,
    schoolUuid
  ) as Promise<TeachersResponse>
}

async function getAllSchoolsTeachersRequest(domainUuid: string): Promise<Response> {
  return await fetch(`${goalbookUrls.pathwaysApi}/domain/${domainUuid}/users/detailed?school=all_schools`, {
    method: 'GET',
    credentials: 'include'
  })
}

export async function getAllSchoolsTeachers(domainUuid: string): Promise<TeachersResponse> {
  const handleRequest = getHandleRequest()
  return handleRequest(
    { requestFn: getAllSchoolsTeachersRequest },
    domainUuid
  ) as Promise<TeachersResponse>
}

async function getNoSchoolAssignedTeachersRequest(domainUuid: string): Promise<Response> {
  return await fetch(`${goalbookUrls.pathwaysApi}/domain/${domainUuid}/users/detailed?school=no_school_assigned`, {
    method: 'GET',
    credentials: 'include'
  })
}

export async function getNoSchoolAssignedTeachers(domainUuid: string): Promise<TeachersResponse> {
  const handleRequest = getHandleRequest()
  return handleRequest(
    { requestFn: getNoSchoolAssignedTeachersRequest },
    domainUuid
  ) as Promise<TeachersResponse>
}

export function downloadTeachersCSV(domainUuid: string, schoolUuid: string, sortedBy?: string): void {
  const params = new URLSearchParams({ school: schoolUuid, format: 'csv' })
  if (sortedBy) {
    params.append('sorted_by', sortedBy)
  }
  const url = `${goalbookUrls.pathwaysApi}/domain/${domainUuid}/users/detailed?${params.toString()}`
  window.location.href = url
}
